import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  type CreateDestinationRequest,
  CreateDestinationRequestSchema,
  DESTINATION_HAULS,
  type DestinationHaul,
  type DestinationListItem,
  type UpdateDestinationRequest,
  UpdateDestinationRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzCombobox,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateDestination, useUpdateDestination } from '../use-destinations';

import * as cityOptionsApi from '@/features/cities/cities.api';
import * as statesApi from '@/features/states/states.api';
import { useTripTypesList } from '@/features/trip-types/use-trip-types';
import { FixedMediaSlotsField } from '@/features/website-cms/components/FixedMediaSlotsField';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

const HAUL_LABELS: Record<DestinationHaul, string> = {
  SHORT_HAUL: 'Short Haul',
  LONG_HAUL: 'Long Haul',
};
const HAUL_NONE = '__haul_none__';
const HAUL_OPTIONS = [
  { value: HAUL_NONE, label: 'Unclassified' },
  ...DESTINATION_HAULS.map((v) => ({ value: v, label: HAUL_LABELS[v] })),
];

// The Region picker was removed from this form (2026-06-24) so Country
// is now the only geography anchor the operator can set - make it
// explicitly required at the form level. The shared request schemas
// still allow region-only payloads for API clients, so we layer the
// requirement on here with the error pinned to the visible `countryId`
// field (the base schema's "region OR country" refine reports on the
// hidden `regionId` path, which would block submit invisibly).
const requireCountry = (v: { countryId?: string | null }): boolean => Boolean(v.countryId);
const COUNTRY_REQUIRED = { message: 'Country is required.', path: ['countryId'] };

// The top cover + all 4 gallery images are mandatory on this form (they
// feed the destination PDF). The shared request schemas keep them optional
// for API clients, so we layer the "required" rule on here - on BOTH the
// create and edit form schemas - with each error pinned to its visible
// field so the operator sees exactly what's missing before submit.
const requireCover = (v: { coverImageMediaId?: string | null }): boolean =>
  Boolean(v.coverImageMediaId);
const COVER_REQUIRED = { message: 'Top cover image is required.', path: ['coverImageMediaId'] };
const requireThreeLandscape = (v: { galleryLandscapeMediaIds?: string[] }): boolean =>
  Array.isArray(v.galleryLandscapeMediaIds) && v.galleryLandscapeMediaIds.length === 3;
const LANDSCAPE_REQUIRED = {
  message: 'Add all 3 horizontal images.',
  path: ['galleryLandscapeMediaIds'],
};
const requirePortrait = (v: { galleryPortraitMediaId?: string | null }): boolean =>
  Boolean(v.galleryPortraitMediaId);
const PORTRAIT_REQUIRED = {
  message: 'Vertical image is required.',
  path: ['galleryPortraitMediaId'],
};

const CreateDestinationFormSchema = CreateDestinationRequestSchema.refine(
  requireCountry,
  COUNTRY_REQUIRED,
)
  .refine(requireCover, COVER_REQUIRED)
  .refine(requireThreeLandscape, LANDSCAPE_REQUIRED)
  .refine(requirePortrait, PORTRAIT_REQUIRED);
const UpdateDestinationFormSchema = UpdateDestinationRequestSchema.refine(
  requireCountry,
  COUNTRY_REQUIRED,
)
  .refine(requireCover, COVER_REQUIRED)
  .refine(requireThreeLandscape, LANDSCAPE_REQUIRED)
  .refine(requirePortrait, PORTRAIT_REQUIRED);

interface DestinationFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  destination?: DestinationListItem | null;
}

/// Sentinel for "no state / no city". Empty strings are forbidden as
/// Radix SelectItem values (see RoleFormDrawer). Region and Country
/// don't need the sentinel: the form treats `''` as "unset" and the
/// schema's `regionId OR countryId` refine surfaces the error on
/// submit.
const NONE = '__none__';

export const DestinationFormDrawer = ({
  open,
  onOpenChange,
  mode,
  destination,
}: DestinationFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateDestination();
  const updateMut = useUpdateDestination();
  const pending = createMut.isPending || updateMut.isPending;

  // `useRegionsList()` was removed 2026-06-24 with the region picker.
  const countriesQuery = useQuery({
    queryKey: ['cities', 'countryOptions'],
    queryFn: () => cityOptionsApi.listCountries(),
    staleTime: 5 * 60_000,
  });

  // Trip-types master for the multi-pick chip row. Always render the
  // active list so the operator can assign one or more on create.
  const tripTypesQuery = useTripTypesList({
    page: 1,
    pageSize: 100,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const tripTypeRows = tripTypesQuery.data?.data ?? [];

  const createForm = useForm<CreateDestinationRequest>({
    resolver: zodResolver(CreateDestinationFormSchema),
    defaultValues: {
      name: '',
      // ADR-XXX (2026-06-24). Region is no longer asked in the UI -
      // every destination ties to a country directly. The shared
      // schema still accepts regionId for back-compat with API
      // clients that send it; the form leaves it `null` (an empty
      // string would fail the `UuidSchema` field validation and block
      // submit on a field that isn't even rendered).
      regionId: null,
      countryId: null,
      stateId: null,
      cityId: null,
      isActive: true,
      // ADR-XXX. Auto-derived from country on change (India = false,
      // any other country = true). No manual toggle.
      isInternational: false,
      haul: null,
      tripTypeIds: [],
      coverImageMediaId: null,
      galleryLandscapeMediaIds: [],
      galleryPortraitMediaId: null,
      sortOrder: 0,
    },
  });
  const updateForm = useForm<UpdateDestinationRequest>({
    resolver: zodResolver(UpdateDestinationFormSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset({
        name: '',
        regionId: null,
        countryId: null,
        stateId: null,
        cityId: null,
        isActive: true,
        isInternational: false,
        haul: null,
        tripTypeIds: [],
        coverImageMediaId: null,
        galleryLandscapeMediaIds: [],
        galleryPortraitMediaId: null,
        sortOrder: 0,
      });
    } else if (destination) {
      updateForm.reset({
        name: destination.name,
        regionId: destination.region?.id ?? null,
        countryId: destination.country?.id ?? null,
        stateId: destination.state?.id ?? null,
        cityId: destination.city?.id ?? null,
        isActive: destination.isActive,
        isInternational: destination.isInternational,
        haul: destination.haul,
        tripTypeIds: destination.tripTypes.map((t) => t.id),
        coverImageMediaId: destination.coverImageMediaId,
        galleryLandscapeMediaIds: destination.galleryLandscapeMediaIds,
        galleryPortraitMediaId: destination.galleryPortraitMediaId,
        sortOrder: destination.sortOrder,
      });
    }
  }, [open, isCreate, destination, createForm, updateForm]);

  // Cascading state/city dropdowns scoped to the currently picked
  // country. Re-fetches on country change.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isCreate ? createForm : updateForm) as ReturnType<typeof useForm<any>>;
  const selectedCountryId = form.watch('countryId') as string | undefined;
  /// State cascade. Hits the full /states list rather than
  /// /states/options because the geography sync (ADR-0041) marks every
  /// bulk-imported state isActive=false; using /states/options would
  /// hide them. pageSize 200 is the shared schema's hard cap and is
  /// plenty - no country has 200+ states.
  const statesQuery = useQuery({
    queryKey: ['destinations', 'stateOptions', selectedCountryId ?? ''],
    queryFn: () =>
      statesApi.listStates({
        page: 1,
        pageSize: 200,
        sortBy: 'name',
        sortDir: 'asc',
        countryId: selectedCountryId,
      }),
    enabled: !!selectedCountryId,
    staleTime: 5 * 60_000,
  });
  /// City cascade. Country-scoped, NOT state-scoped: picking a state
  /// doesn't affect which cities are shown. Country has many cities
  /// (India ~4k after geography sync); pageSize 200 is the schema cap,
  /// so we drive a server-side search to keep the picker useful for
  /// cities past the alphabetical first slice. We don't pass
  /// `isActive: true` because the bulk-imported geography is inactive
  /// by default - the operator still needs to PICK from it when
  /// building destinations.
  const [citySearch, setCitySearch] = useState('');
  const citiesQuery = useQuery({
    queryKey: ['destinations', 'cityOptions', selectedCountryId ?? '', citySearch],
    queryFn: () =>
      cityOptionsApi.listCities({
        page: 1,
        pageSize: 200,
        sortBy: 'name',
        sortDir: 'asc',
        countryId: selectedCountryId,
        ...(citySearch.trim().length > 0
          ? { search: citySearch.trim(), searchMode: 'prefix' as const }
          : {}),
      }),
    enabled: !!selectedCountryId,
    staleTime: 5 * 60_000,
  });

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  // ADR-XXX (2026-06-24): auto-derive `isInternational` from the
  // picked country. Home country (India) = domestic; everything
  // else = international. The form's manual toggle was removed; the
  // computed value lives in form state so the submit payload still
  // carries the right flag. Same effect re-runs on country change
  // to keep the value honest.
  useEffect(() => {
    if (!selectedCountryId) return;
    const country = (countriesQuery.data ?? []).find((c) => c.id === selectedCountryId);
    if (!country) return;
    const isHome = country.isoCode.toLowerCase() === 'in';
    const nextIntl = !isHome;
    const currentIntl = form.getValues('isInternational') as boolean | undefined;
    if (currentIntl !== nextIntl) {
      form.setValue('isInternational', nextIntl, { shouldDirty: true });
    }
    const nextHaul: DestinationHaul = nextIntl ? 'LONG_HAUL' : 'SHORT_HAUL';
    const currentHaul = form.getValues('haul') as DestinationHaul | null | undefined;
    if (currentHaul !== nextHaul) {
      form.setValue('haul', nextHaul, { shouldDirty: true });
    }
    // The deps cover what we read; `form` is stable per RHF.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryId, countriesQuery.data]);
  const stateOptions = useMemo(() => {
    const opts = [
      { value: NONE, label: 'Country-wide (no state)' },
      ...(statesQuery.data?.data ?? []).map((s) => ({
        value: s.id,
        label: s.isActive ? s.name : `${s.name} (inactive)`,
      })),
    ];
    // Keep the destination's saved state selectable even if the slice misses it.
    const saved = destination?.state;
    if (saved && !opts.some((o) => o.value === saved.id)) {
      opts.splice(1, 0, { value: saved.id, label: saved.name });
    }
    return opts;
  }, [statesQuery.data, destination]);
  const cityOptions = useMemo(() => {
    const opts = [
      { value: NONE, label: 'No specific city' },
      ...(citiesQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: c.isActive ? c.name : `${c.name} (inactive)`,
      })),
    ];
    // The city list is only the first server slice (or a search result), so a
    // saved city outside it would render the field empty. Prepend it.
    const saved = destination?.city;
    if (saved && !opts.some((o) => o.value === saved.id)) {
      opts.splice(1, 0, { value: saved.id, label: saved.name });
    }
    return opts;
  }, [citiesQuery.data, destination]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // Empty-string sentinels (used so Radix Select can render an
    // "unset" state without throwing) get normalised to null before
    // the wire. The schema's `region OR country` refine has already
    // ensured at least one of them resolves to a uuid.
    const payload: CreateDestinationRequest = {
      ...values,
      regionId: values.regionId || null,
      countryId: values.countryId || null,
      stateId: values.stateId || null,
      cityId: values.cityId || null,
      isInternational: values.isInternational ?? false,
      haul: values.haul ?? null,
      tripTypeIds: values.tripTypeIds ?? [],
    };
    try {
      await createMut.mutateAsync(payload);
      onOpenChange(false);
    } catch {
      /* toast already fired in mutation hook */
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!destination) return;
    const payload: UpdateDestinationRequest = {
      name: values.name,
      regionId: values.regionId || null,
      countryId: values.countryId || null,
      stateId: values.stateId || null,
      cityId: values.cityId || null,
      isActive: values.isActive,
      isInternational: values.isInternational,
      // Send `null` when the operator picked "Unclassified" so the
      // server clears the column; omit (undefined) leaves it alone.
      haul: values.haul === undefined ? undefined : values.haul,
      // Always send the current array; `[]` clears the m2m, anything
      // else overwrites - both are correct PATCH semantics.
      tripTypeIds: values.tripTypeIds ?? [],
      coverImageMediaId: values.coverImageMediaId,
      galleryLandscapeMediaIds: values.galleryLandscapeMediaIds,
      galleryPortraitMediaId: values.galleryPortraitMediaId,
      sortOrder: values.sortOrder,
    };
    try {
      await updateMut.mutateAsync({ id: destination.id, payload });
      onOpenChange(false);
    } catch {
      /* toast already fired */
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'New destination' : `Edit ${destination?.name ?? 'destination'}`}
      description="A pre-canned travel target operators pick when capturing a lead."
      contentClassName="sm:w-[720px]! sm:max-w-none!"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Create destination' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={isCreate ? onCreate : onUpdate}>
        <UzFormField name="name">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Display name"
              placeholder="Bali Beach Holiday"
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        {/* Region picker was removed 2026-06-24. Every destination
            ties to a country directly; the shared schema's
            "regionId OR countryId" refine is now satisfied by
            countryId alone. */}
        <UzFormField name="countryId">
          {({ field, error }) => (
            <UzSelect<string>
              name={field.name}
              label="Country"
              required
              placeholder={countriesQuery.isLoading ? 'Loading…' : 'Pick a country'}
              options={countryOptions}
              value={field.value || undefined}
              onValueChange={(raw) => {
                if (raw === field.value) return;
                field.onChange(raw);
                // Reset state + city when country changes - the old ids
                // would belong to the wrong hierarchy.
                form.setValue('stateId', null);
                form.setValue('cityId', null);
                setCitySearch('');
              }}
              error={error}
              disabled={pending || countriesQuery.isLoading}
            />
          )}
        </UzFormField>
        <UzFormField name="stateId">
          {({ field, error }) => (
            <UzSelect<string>
              name={field.name}
              label="State (optional)"
              hint={selectedCountryId ? undefined : 'Pick a country first.'}
              options={stateOptions}
              value={field.value ?? NONE}
              onValueChange={(v) => {
                const next = v === NONE ? null : v;
                field.onChange(next);
                // City is country-scoped, not state-scoped, so changing
                // the state intentionally does NOT clear cityId.
              }}
              error={error}
              disabled={pending || !selectedCountryId || statesQuery.isLoading}
            />
          )}
        </UzFormField>
        <UzFormField name="cityId">
          {({ field, error }) => (
            <UzCombobox<string>
              name={field.name}
              label="City (optional)"
              hint={
                !selectedCountryId
                  ? 'Pick a country first.'
                  : 'Type to search - results are server-filtered.'
              }
              placeholder="Select a city"
              searchPlaceholder="Type to search cities..."
              options={cityOptions}
              value={field.value ?? NONE}
              onValueChange={(v) => field.onChange(v === NONE ? null : v)}
              // Server-side search: parent owns the filter, local
              // fuzzy-match is off so the dropdown faithfully shows the
              // 200-row server slice for the current query.
              onSearchChange={setCitySearch}
              loading={citiesQuery.isLoading || citiesQuery.isFetching}
              emptyText={citySearch ? 'No matches' : 'Type to search'}
              error={error}
              disabled={pending || !selectedCountryId}
            />
          )}
        </UzFormField>
        {/* ----- Coverage dimensions ----- */}
        {/* `isInternational` is auto-derived from the picked country
            (India = false, else true) by the effect above. The manual
            Switch was removed 2026-06-24 to keep the data honest. */}
        <UzFormField name="haul">
          {({ field, error }) => (
            <UzSelect<string>
              name={field.name}
              label="Haul"
              hint="Travel-time tier. Leave unclassified if unknown."
              options={HAUL_OPTIONS}
              value={(field.value as DestinationHaul | null | undefined) ?? HAUL_NONE}
              onValueChange={(v) => field.onChange(v === HAUL_NONE ? null : (v as DestinationHaul))}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="tripTypeIds">
          {({ field }) => {
            const current = (field.value as string[] | undefined) ?? [];
            const toggle = (id: string): void => {
              if (pending) return;
              if (current.includes(id)) field.onChange(current.filter((x) => x !== id));
              else field.onChange([...current, id]);
            };
            return (
              <div className="space-y-1">
                <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Trip types
                </label>
                {tripTypesQuery.isLoading ? (
                  <p className="text-xs text-zinc-500">Loading…</p>
                ) : tripTypeRows.length === 0 ? (
                  <p className="text-xs text-zinc-500">
                    No active trip types in the master. Create some under Masters &rarr; Trip Types.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tripTypeRows.map((t) => {
                      const checked = current.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggle(t.id)}
                          disabled={pending}
                          className={
                            'rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
                            (checked
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50')
                          }
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-zinc-500">
                  Toggle one or more. Surfaces in the user-form destination filter.
                </p>
              </div>
            );
          }}
        </UzFormField>
        <UzFormField name="coverImageMediaId">
          {({ field, error }) => (
            <div className="space-y-1">
              <MediaDropField
                label="Top cover image *"
                value={field.value as string | null | undefined}
                onChange={(id) => field.onChange(id)}
                kind="image"
                variant="banner"
                folder="destinations"
                disabled={pending}
              />
              {error ? (
                <p className="text-xs text-(--ds-error)">{error}</p>
              ) : (
                <p className="text-xs text-zinc-500">
                  Required. Shown as this destination&apos;s cover on the website.
                </p>
              )}
            </div>
          )}
        </UzFormField>
        {/* ----- Gallery: 3 landscape (horizontal, drag to reorder) +
            1 portrait (vertical). These feed the generated destination PDF,
            so the landscape order and the portrait slot are meaningful.
            `data-vaul-no-drag` stops the vaul drawer from hijacking the
            dnd-kit reorder gesture as a swipe-to-dismiss (which slid the
            whole panel). Vaul matches it via closest(), so one wrapper
            covers every draggable thumbnail inside. ----- */}
        <div data-vaul-no-drag className="space-y-3 rounded-md border border-zinc-200 p-3">
          <div>
            <div className="text-xs font-medium tracking-wide text-zinc-500 uppercase">Gallery</div>
            <p className="mt-0.5 text-xs text-zinc-500">
              Images used to compose this destination&apos;s PDF.
            </p>
          </div>
          <UzFormField name="galleryLandscapeMediaIds">
            {({ field, error }) => (
              <div className="space-y-1">
                <FixedMediaSlotsField
                  label="Horizontal images (all 3 required) *"
                  value={(field.value as string[] | null | undefined) ?? []}
                  onChange={(ids) => field.onChange(ids)}
                  count={3}
                  gridClassName="grid grid-cols-2 gap-2 sm:grid-cols-3"
                  folder="destinations"
                  disabled={pending}
                />
                {error ? (
                  <p className="text-xs text-(--ds-error)">{error}</p>
                ) : (
                  <p className="text-xs text-zinc-500">
                    Landscape shots. Drag a filled tile to reorder - the order is used in the PDF.
                  </p>
                )}
              </div>
            )}
          </UzFormField>
          <UzFormField name="galleryPortraitMediaId">
            {({ field, error }) => (
              <div className="space-y-1">
                <MediaDropField
                  label="Vertical image (portrait) *"
                  value={field.value as string | null | undefined}
                  onChange={(id) => field.onChange(id)}
                  kind="image"
                  variant="banner"
                  folder="destinations"
                  disabled={pending}
                />
                {error ? (
                  <p className="text-xs text-(--ds-error)">{error}</p>
                ) : (
                  <p className="text-xs text-zinc-500">
                    Required. The tall final image - the 4th slot in the PDF layout.
                  </p>
                )}
              </div>
            )}
          </UzFormField>
        </div>
        <UzFormField name="sortOrder">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Sort order"
              value={field.value ?? 0}
              onValueChange={(v) => field.onChange(v ?? 0)}
              min={0}
              max={9999}
              step={10}
              hint="Lower values appear first in the picker."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="isActive">
          {({ field }) => (
            <div className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-zinc-900">Active</div>
                <div className="text-xs text-zinc-500">
                  Inactive destinations are hidden from the lead form picker.
                </div>
              </div>
              <UzSwitch
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
                disabled={pending}
              />
            </div>
          )}
        </UzFormField>
      </UzForm>
    </UzDrawer>
  );
};
